// src/hooks/useAutoSave.ts - Auto-save functionality for BCP form
import { useCallback, useRef, useState, useEffect } from 'react';
import { FormState, SaveStatus } from '../types/bcp';
import { updateBCP } from '../services/api';

export interface AutoSaveHook {
  autoSave: (data: Partial<FormState>) => void;
  autoSaveStatus: Pick<SaveStatus, 'isAutoSaving' | 'lastSaved' | 'errors'>;
}

export const useAutoSave = (
  bcpId: string | undefined,
  isEditing: boolean
): AutoSaveHook => {
  const [autoSaveStatus, setAutoSaveStatus] = useState({
    isAutoSaving: false,
    lastSaved: null as Date | null,
    errors: {} as { [section: string]: string | null }
  });

  // Refs for debouncing and cleanup
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAutoSaveDataRef = useRef<string>('');

  // Auto-save function with debouncing
  const autoSave = useCallback(async (data: Partial<FormState>) => {
    if (!isEditing || !bcpId) return;

    // Validate required Step 1 fields before auto-saving
    if (!data.business_name?.trim() ||
        !data.business_type?.trim() ||
        !data.industry?.trim() ||
        !data.employee_count ||
        data.employee_count <= 0) {
      console.log('🚫 [AUTO SAVE] Skipping auto-save - required Step 1 fields missing');
      return;
    }

    // Debounce auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      // Check if data actually changed
      const currentDataString = JSON.stringify(data);
      if (currentDataString === lastAutoSaveDataRef.current) return;

      try {
        setAutoSaveStatus(prev => ({ ...prev, isAutoSaving: true }));

        // Only include auto-saveable fields
        const autoSaveableFields = {
          business_name: data.business_name,
          business_type: data.business_type,
          industry: data.industry,
          employee_count: data.employee_count,
          annual_revenue: data.annual_revenue,
          primary_location: data.primary_location,
          additional_locations: data.additional_locations,
          // Note: Critical functions, suppliers, IT systems are handled by manual save
        };

        await updateBCP(parseInt(bcpId), autoSaveableFields as any);

        lastAutoSaveDataRef.current = currentDataString;
        setAutoSaveStatus(prev => ({
          ...prev,
          isAutoSaving: false,
          lastSaved: new Date(),
          errors: { ...prev.errors, autoSave: null }
        }));

      } catch (error) {
        console.error('Auto-save failed:', error);
        setAutoSaveStatus(prev => ({
          ...prev,
          isAutoSaving: false,
          errors: { ...prev.errors, autoSave: 'Auto-save failed' }
        }));
      }
    }, 1500); // 1.5 second debounce
  }, [isEditing, bcpId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  return {
    autoSave,
    autoSaveStatus
  };
};
